import { useEffect, useMemo, useState } from 'react';
import api from '@/data/api';
import Toast from '@/utils/toastManager';
import VideoThumbnailCard from '@/v2/components/VideoThumbnailCard';
import TRC_CONTENT from '@/constants/trcContentType';
import Filter from '@/Asset/images/icon/Filters-lines.svg';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import FilterModal from '@/component/FilterModal';
import { useLocation } from 'react-router-dom';
import { TRC_ROUTES } from '@/TRC/constants/trcRoutes';
import { LESSON_PLAN_LABELS } from '@/constants/lessonPlanLabels';
import TRCLessonPlans from '@/TRC/pages/LessonPlans/LessonPlans';
import TRC_PAGE_CATEGORIES from '@/TRC/constants/trcCategories';
import './LessonPlanList.css';

const STUDIO_HUB_TABS = [
	{ id: 'lesson-plans', label: 'Lesson Plans' },
	{ id: 'progressive-lesson-plans', label: 'Progressive Lesson Plans' },
	{ id: 'view-all', label: 'View All' }
];

const getCreatedAtTime = item => {
	const time = new Date(item?.createdAt || item?.updatedAt || 0).getTime();
	return Number.isNaN(time) ? 0 : time;
};

const StudioHubToggle = ({ activeTab, setActiveTab }) => (
	<div className="studio-hub-toggle-container">
		<div className="studio-hub-toggle-chips">
			{STUDIO_HUB_TABS.map(tab => (
				<button
					key={tab.id}
					onClick={() => setActiveTab(tab.id)}
					className={`studio-hub-chip-btn ${activeTab === tab.id ? 'active' : ''}`}
				>
					{tab.label}
				</button>
			))}
		</div>
	</div>
);

const LessonPlanList = ({ title = 'Lesson Plans', basePath = 'lesson-plan' }) => {
	const { pathname } = useLocation();
	const isStudioHubPath = pathname.startsWith(TRC_ROUTES.STUDIO_HUB_HOME);
	const isStudioHubLessonPlans = pathname === `${TRC_ROUTES.STUDIO_HUB_HOME}/${TRC_ROUTES.LESSON_PLANS}`;
	const isStudioHubProgressiveLessonPlans = pathname === `${TRC_ROUTES.STUDIO_HUB_HOME}/${TRC_ROUTES.PROGRESSIVE_LESSON_PLANS}`;
	const shouldShowStudioHubToggle = isStudioHubLessonPlans || isStudioHubProgressiveLessonPlans;
	const [activeTab, setActiveTab] = useState(
		isStudioHubProgressiveLessonPlans ? 'progressive-lesson-plans' : 'view-all'
	);
	const [lessonPlans, setLessonPlans] = useState([]);
	const [trcLessonPlans, setTrcLessonPlans] = useState([]);
	const [selectedTags, setSelectedTags] = useState([]);
	const [selectedInstructors, setSelectedInstructors] = useState([]);
	const [selectedProducts, setSelectedProducts] = useState([]);
	const [sortby, setSortby] = useState('latest');
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

	useEffect(() => {
		async function fetch() {
			try {
				const [progressiveResponse, trcResponse] = await Promise.all([
					api.getLessonPlans(),
					shouldShowStudioHubToggle
						? api.fetchContent({ category: TRC_PAGE_CATEGORIES.LESSON_PLANS })
						: Promise.resolve({ data: [] })
				]);
				setLessonPlans(progressiveResponse?.data || []);
				setTrcLessonPlans(trcResponse?.data || []);
			} catch (err) {
				Toast.error(err.message || 'Something went wrong');
			}
		}
		fetch();
	}, [shouldShowStudioHubToggle]);

	useEffect(() => {
		if (isStudioHubProgressiveLessonPlans) {
			setActiveTab('progressive-lesson-plans');
		} else if (isStudioHubLessonPlans) {
			setActiveTab('view-all');
		}
	}, [isStudioHubLessonPlans, isStudioHubProgressiveLessonPlans]);

	const activePlans = useMemo(() => {
		if (!shouldShowStudioHubToggle) return lessonPlans;

		const normalizedTrcLessonPlans = trcLessonPlans.map(plan => ({
			...plan,
			basePath: 'studio-hub/lesson-plans',
			route: TRC_ROUTES.LESSON_PLANS
		}));

		const normalizedProgressiveLessonPlans = lessonPlans.map(plan => ({
			...plan,
			fileType: 'pdf',
			basePath: 'studio-hub/progressive-lesson-plans',
			route: TRC_ROUTES.PROGRESSIVE_LESSON_PLANS,
			categoryDetails: [
				{ _id: 'studio-lesson-plan', name: LESSON_PLAN_LABELS.PROGRESSIVE, color: '#008bc3' },
				...(plan.categoriesDetails || [])
			]
		}));

		if (activeTab === 'lesson-plans') return normalizedTrcLessonPlans;
		if (activeTab === 'progressive-lesson-plans') return normalizedProgressiveLessonPlans;
		return [...normalizedTrcLessonPlans, ...normalizedProgressiveLessonPlans];
	}, [activeTab, lessonPlans, shouldShowStudioHubToggle, trcLessonPlans]);

	const allTags = useMemo(() => {
		const tagsById = new Map();
		activePlans.forEach(plan =>
			(plan.tagsDetails || plan.tags || []).forEach(tag => tagsById.set(tag._id, tag))
		);
		return [...tagsById.values()];
	}, [activePlans]);

	const allInstructors = useMemo(() =>
		[...new Set(lessonPlans.map(p => p.instructor).filter(Boolean))],
		[lessonPlans]);

	const allProducts = useMemo(() => {
		const byId = new Map();
		activePlans.forEach(plan =>
			(plan.productsData || []).forEach(p => byId.set(p._id, { id: p._id, name: p.title || p.name, subname: p.subname || '' }))
		);
		return [...byId.values()];
	}, [activePlans]);

	const filteredPlans = useMemo(() => {
		let result = activePlans.filter(plan => {
			if (selectedTags.length && !(plan.tagsDetails || plan.tags || []).some(tag => selectedTags.includes(tag._id))) return false;
			if (selectedInstructors.length && !selectedInstructors.includes(plan.instructor)) return false;
			if (selectedProducts.length) {
				const planProductKeys = (plan.productsData || []).map(p => p.subname || p.title || p.name);
				if (!selectedProducts.some(key => planProductKeys.includes(key))) return false;
			}
			return true;
		});

		result.sort((a, b) => {
			if (sortby === 'oldest') return getCreatedAtTime(a) - getCreatedAtTime(b);
			if (sortby === 'title_asc') return (a.title || '').localeCompare(b.title || '');
			if (sortby === 'title_desc') return (b.title || '').localeCompare(a.title || '');
			return getCreatedAtTime(b) - getCreatedAtTime(a);
		});
		return result;
	}, [activePlans, selectedTags, selectedInstructors, selectedProducts, sortby]);

	const handleApplyFilter = (tags, _durations, _levels, instructors, products) => {
		setSelectedTags(tags);
		setSelectedInstructors(instructors);
		setSelectedProducts(products);
		setIsFilterModalOpen(false);
	};

	const resolvedBasePath = isStudioHubPath
		? 'studio-hub/progressive-lesson-plans'
		: basePath;

	const displayTitle = shouldShowStudioHubToggle
		? 'Lesson Plans'
		: title;

	if (shouldShowStudioHubToggle && activeTab === 'lesson-plans') {
		return (
			<TRCLessonPlans
				basePath="studio-hub/lesson-plans"
				topContent={<StudioHubToggle activeTab={activeTab} setActiveTab={setActiveTab} />}
			/>
		);
	}

	return (
		<div className="d-flex flex-column gap-2">
			{/* Desktop filter bar */}
			<div className="c-tab_cata-text d-none d-lg-flex">
				<div id="c-mobile_tab_hide" className="c-tab_cata mb-n9">
					<div className="row m-0 c-tab_box">
						<div className="col p-0">
							<div className="c-filter_btn c-filter_btn-lessons">
								<div className="c-filter_btn-one c-filter_btn-tags unique" style={{ cursor: 'pointer', padding: '0' }}>
									<span
										onClick={() => setIsFilterModalOpen(true)}
										className="c-filters-btn"
										style={{ padding: '0.63rem 0.75rem', display: 'flex', justifyContent: 'space-between' }}
									>
										<img src={Filter} alt="filter icon" />
										Filters
									</span>
									{isFilterModalOpen && (
										<FilterModal
											isOpen={isFilterModalOpen}
											tags={allTags}
											checkedTags={selectedTags}
											instructors={allInstructors}
											checkedInstructors={selectedInstructors}
											products={allProducts}
											checkedProducts={selectedProducts}
											handleApplyFilter={handleApplyFilter}
											handleClose={() => setIsFilterModalOpen(false)}
											showDurationFilter={false}
											showLevelFilter={false}
											contentType={TRC_CONTENT.TRC}
										/>
									)}
								</div>
								<div className="c-filter_btn-one">
									<ImportExportIcon size={24} />
									<span>Sort by</span>
									<select className="filterby" onChange={e => setSortby(e.target.value)} value={sortby}>
										<option value="latest">Newest</option>
										<option value="oldest">Oldest</option>
										<option value="title_asc">Title (A - Z)</option>
										<option value="title_desc">Title (Z - A)</option>
									</select>
								</div>
							</div>
						</div>
					</div>
				</div>
				<h4>{displayTitle}</h4>
			</div>
			{/* Mobile header */}
			<div className="d-flex justify-content-between align-items-center d-lg-none pt-2">
				<h4 className="w-100 px-2 text-white fw-bold m-0" style={{ fontFamily: 'Good Times' }}>{displayTitle}</h4>
			</div>
			{shouldShowStudioHubToggle && <StudioHubToggle activeTab={activeTab} setActiveTab={setActiveTab} />}
			<div className="c-video_list">
				<div className="row m-0">
					{filteredPlans.map(plan => (
						<div key={plan._id} className="mb-2">
							<VideoThumbnailCard
								content={{ ...plan, thumbnailLink: plan.thumbnailLink, fileType: plan.fileType || 'pdf' }}
								route={plan.route || 'lesson-plan'}
								basePath={plan.basePath || resolvedBasePath}
								showSaveButton={false}
								showAddToPlaylist={false}
								type={TRC_CONTENT.TRC}
								customStyles={{ width: '100%', aspectRatio: '16/9' }}
							/>
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

export default LessonPlanList;
